import React, { useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Music2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import PoemDisplay from '../components/PoemDisplay';
import { POEMS } from '../data/poems';

const PoemPage = () => {
  const { poemId } = useParams();
  const navigate = useNavigate();
  const idx = POEMS.findIndex((p) => p.id === poemId);
  const poem = idx >= 0 ? POEMS[idx] : null;
  const prev = idx > 0 ? POEMS[idx - 1] : POEMS[POEMS.length - 1];
  const next = idx >= 0 && idx < POEMS.length - 1 ? POEMS[idx + 1] : POEMS[0];

  useEffect(() => {
    if (poem) {
      document.title = `${poem.title} \u2014 ${poem.author} | 40 Songs and a Mic`;
    }
    return () => {
      document.title = '40 Songs and a Mic | Gospel & Hip-Hop Healing';
    };
  }, [poem]);

  if (!poem) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <h1 className="text-4xl font-bold text-white mb-3" style={{ fontFamily: '"Bebas Neue", sans-serif' }}>
            Poem not found
          </h1>
          <p className="text-gray-400 mb-6">
            That link may have moved. Head back to the Poetry Room to find it.
          </p>
          <Button
            onClick={() => navigate('/poetry')}
            data-testid="poem-not-found-cta"
            className="bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0a0a0a] hover:from-[#FFD700] hover:to-[#D4AF37] font-bold"
          >
            Go to the Poetry Room
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Header bar */}
      <header className="sticky top-0 z-50 bg-[#0a0a0a]/90 backdrop-blur-lg border-b border-[#D4AF37]/20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link
            to="/poetry"
            className="flex items-center gap-2 text-gray-400 hover:text-[#D4AF37] transition-colors"
            data-testid="poem-back-to-room"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">The Poetry Room</span>
          </Link>
          <Link to="/" className="flex items-center gap-2 hover:text-[#D4AF37] transition-colors">
            <Music2 className="w-5 h-5 text-[#D4AF37]" />
            <span className="text-white font-bold hidden sm:inline" style={{ fontFamily: '"Bebas Neue", sans-serif' }}>
              40 SONGS AND A MIC
            </span>
          </Link>
        </div>
      </header>

      {/* Poem */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        <PoemDisplay poem={poem} size="lg" showShare showPermalink={false} />

        {/* Prev / Next navigation */}
        <nav className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Link
            to={`/poetry/${prev.id}`}
            data-testid="poem-nav-prev"
            className="group bg-[#1a1a1a] border border-[#D4AF37]/20 hover:border-[#D4AF37] rounded-2xl p-5 transition-colors"
          >
            <p className="text-xs uppercase tracking-widest text-gray-500 mb-1 flex items-center gap-1">
              <ArrowLeft className="w-3 h-3" /> Previous
            </p>
            <p className="text-white font-bold group-hover:text-[#D4AF37] transition-colors" style={{ fontFamily: '"Bebas Neue", sans-serif' }}>
              {prev.title}
            </p>
          </Link>
          <Link
            to={`/poetry/${next.id}`}
            data-testid="poem-nav-next"
            className="group bg-[#1a1a1a] border border-[#D4AF37]/20 hover:border-[#D4AF37] rounded-2xl p-5 transition-colors text-right"
          >
            <p className="text-xs uppercase tracking-widest text-gray-500 mb-1 flex items-center gap-1 justify-end">
              Next <ArrowRight className="w-3 h-3" />
            </p>
            <p className="text-white font-bold group-hover:text-[#D4AF37] transition-colors" style={{ fontFamily: '"Bebas Neue", sans-serif' }}>
              {next.title}
            </p>
          </Link>
        </nav>

        {/* Bottom CTA */}
        <div className="mt-16 text-center bg-gradient-to-r from-[#1a1a1a] via-[#D4AF37]/5 to-[#1a1a1a] border border-[#D4AF37]/30 rounded-3xl p-8">
          <h3 className="text-2xl md:text-3xl font-bold text-white mb-2" style={{ fontFamily: '"Bebas Neue", sans-serif' }}>
            Read all of J&rsquo;Wells&rsquo; poems
          </h3>
          <p className="text-gray-400 mb-6">
            Or pick what you are carrying today and let a verse meet you there.
          </p>
          <Link to="/poetry" className="inline-block max-w-full">
            <Button
              data-testid="poem-cta-room"
              className="w-full sm:w-auto max-w-full bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0a0a0a] hover:from-[#FFD700] hover:to-[#D4AF37] font-bold px-6 sm:px-8 py-6 text-base sm:text-base whitespace-normal leading-tight break-words"
            >
              ENTER THE POETRY ROOM
            </Button>
          </Link>
        </div>
      </main>
    </div>
  );
};

export default PoemPage;
